import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';

import { Plan, Prices, PromotionModel, TYPE_MARKETING } from '@bytel/bytel-sales';

import { MainCartService } from '@services/main-cart.service';
import { MessageService } from '@services/message.service';
import { OptionsService } from '@services/options.service';

import { ModalOfferDetailsComponent } from '@tunnel/cart/mixed-cart/modal-offer-details/modal-offer-details.component';

import { AddPlanConfig } from '@interfaces/checkout.interface';

import { ACTION_CLICK } from '@shared/tag/tag.interface';
import { TagService } from '@shared/tag/tag.service';

import { DataCyDirective } from '../../../../../common/directives/data-cy.directive';
import { ObligationPipe } from '../../../../../shared/pipes/obligation.pipe';
import { PricesBlockComponent } from '../../../subscription-block/subscription/prices-block/prices-block/prices-block.component';
import { CardOfferHeaderComponent } from './card-offer-header/card-offer-header.component';

@Component({
    selector: 'checkout-card-offer',
    templateUrl: './card-offer.component.html',
    styleUrl: 'card-offer.component.scss',
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: true,
    imports: [DataCyDirective, PricesBlockComponent, NgClass, ObligationPipe, CardOfferHeaderComponent],
})
export class CardOfferComponent implements OnInit {
    public isLocked: boolean = false;
    public qty: number;
    public prices: Prices;
    public useSpecialStyle: boolean = false;
    public promoHome: PromotionModel;
    public promoHomeWording: string = '';

    @Input() public isLoading: boolean = false;
    @Input() public plan: Plan;
    @Input() public quoteIndex: number = 1;
    @Input() public cardStyle: 'unique' | 'multiple' = 'multiple';
    @Input() public showAction: boolean = true;
    @Input() public cartForm: FormGroup;
    @Input() public isUniq: boolean = false;
    @Input() public atLeastOneOfferHasPromoHome: boolean = false;
    @Input() public atLeastOneBlockablePlan: boolean = false;

    @Output() public addProductEvent = new EventEmitter<AddPlanConfig>();
    @Output() public removeProductEvent = new EventEmitter<string>();
    @Output() public setLockedEvent = new EventEmitter<boolean>();

    protected cartService: MainCartService = inject(MainCartService);
    protected messageService: MessageService = inject(MessageService);
    protected tagService = inject(TagService);

    public lockedToggle(): void {
        this.setLocked(!this.isLocked);
    }

    public setLocked(value: boolean): void {
        this.isLocked = value;
        this.setLockedEvent.emit(value);
    }

    public ngOnInit(): void {
        this.qty = this._getQty();
        this.cartService.cartQuoteSubscription.subscribe(() => {
            this.qty = this._getQty();
        });
        if (this.qty) {
            this.isLocked = OptionsService.HasBlockedOption(this.cartService.cartModel, this.quoteIndex);
        }
        this.promoHome = this.plan.promotions.automatic.find((promo) =>
            promo.typesMarketing.includes(TYPE_MARKETING.FOYER),
        );
        if (this.promoHome) {
            this.promoHomeWording = this.promoHome.fanion || `${-this.promoHome.amount}€/mois déjà déduit`;
        }
    }

    public add(): void {
        this.sendTagAction('Choisir_ce_forfait');
        this.addProductEvent.emit({
            plan: this.plan,
            isLocked: this.isLocked,
        });
    }

    public remove(): void {
        this.sendTagAction('Supprimer');
        this.removeProductEvent.emit(this.plan.gencode);
        this.qty = 0;
    }

    public displayDetails(event: MouseEvent): void {
        this.sendTagAction('En_savoir_plus');
        event.stopPropagation();
        this.messageService.openModal(
            ModalOfferDetailsComponent,
            {
                title: this.plan.label,
                isOwned: this.qty > 0,
                plan: this.plan,
                isLocked: this.isLocked,
                addPlan: () => this.add(),
            },
            'small',
            {
                lockedToggleEvent: (value: boolean) => this.setLocked(value),
            },
        );
    }

    private _getQty(): number {
        return this.cartService.getProductQty(this.plan);
    }

    private sendTagAction(clickName: string): void {
        this.tagService.sendEvent(
            `${clickName}_${this.plan.gencode}`,
            'Banniere_panier_mixte_forfait',
            ACTION_CLICK.SELECTION,
        );
    }
}
